import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, StatusBadge } from "@/components/bits";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg, fmtDate } from "@/lib/api";

const CATEGORIES = ["recruitment", "hrms", "payroll", "payment", "bgv", "course", "franchise", "technical", "other"];
const ADMIN_ROLES = ["super_admin", "admin", "finance_admin", "hr_admin", "support"];

export default function Support() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const [tickets, setTickets] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "technical", subject: "", message: "" });
  const [replyFor, setReplyFor] = useState(null);
  const [reply, setReply] = useState("");
  const [replyStatus, setReplyStatus] = useState("in_progress");
  const [busy, setBusy] = useState(false);

  const load = () => {
    const url = isAdmin ? `/admin/tickets${statusFilter ? `?status=${statusFilter}` : ""}` : "/support/tickets/mine";
    api.get(url).then(({ data }) => setTickets(data.tickets)).catch(() => setTickets([]));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, isAdmin]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/support/tickets", form);
      toast.success(`Ticket ${data.ticket_no} created`);
      setOpen(false);
      setForm({ category: "technical", subject: "", message: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async () => {
    try {
      await api.post(`/support/tickets/${replyFor.id}/reply`,
        isAdmin ? { message: reply, status: replyStatus } : { message: reply });
      toast.success("Reply sent");
      setReplyFor(null);
      setReply("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="support-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <p className="text-sm text-muted-foreground">
          {isAdmin ? "All platform support tickets." : "Raise a ticket and track replies from the Job Spring support team."}
        </p>
        <div className="flex gap-3">
          {isAdmin && (
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40" data-testid="tickets-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {["open", "in_progress", "resolved", "closed"].map((s) => (
                  <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {!isAdmin && (
            <Button onClick={() => setOpen(true)} className="rounded-full bg-[#0B1B3D]" data-testid="new-ticket-btn">
              <LifeBuoy className="w-4 h-4 mr-1.5" /> New Ticket
            </Button>
          )}
        </div>
      </div>

      {!tickets ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : tickets.length === 0 ? (
        <EmptyState title="No tickets" description={isAdmin ? "Support tickets from users will appear here." : "Need help? Raise a ticket and our team will respond."} testId="tickets-empty" />
      ) : (
        <div className="space-y-3" data-testid="tickets-list">
          {tickets.map((t) => (
            <button
              key={t.id}
              onClick={() => { setReplyFor(t); setReply(""); setReplyStatus(t.status === "open" ? "in_progress" : t.status); }}
              className="w-full text-left bg-white border border-border/60 rounded-xl p-5 card-hover"
              data-testid={`ticket-${t.id}`}
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="font-semibold text-sm text-[#0B1B3D]">{t.ticket_no} — {t.subject}</p>
                  <p className="text-xs text-muted-foreground mt-1 capitalize">
                    {t.category} · {isAdmin && `${t.user_name} (${t.user_role}) · `}{fmtDate(t.created_at)} · {t.replies.length} repl{t.replies.length === 1 ? "y" : "ies"}
                  </p>
                </div>
                <StatusBadge status={t.status} />
              </div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="ticket-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>New support ticket</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5" data-testid="ticket-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5" data-testid="ticket-subject" /></div>
            <div><Label>Describe the issue</Label><Textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" data-testid="ticket-message" /></div>
            <Button onClick={create} disabled={busy || !form.subject.trim() || !form.message.trim()} className="w-full rounded-full bg-[#0B1B3D]" data-testid="ticket-submit-btn">
              {busy ? "Submitting…" : "Submit Ticket"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!replyFor} onOpenChange={() => setReplyFor(null)}>
        <DialogContent className="max-w-lg" data-testid="reply-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{replyFor?.ticket_no} — {replyFor?.subject}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            <div className="text-sm bg-[#F8FAFC] border border-border/60 rounded-lg p-3">
              <p>{replyFor?.message}</p>
              <p className="text-xs text-muted-foreground mt-1.5">— {replyFor?.user_name} · {fmtDate(replyFor?.created_at)}</p>
            </div>
            {replyFor?.replies.map((r) => (
              <div key={r.id} className={`text-sm border rounded-lg p-3 ${ADMIN_ROLES.includes(r.by_role) ? "bg-[#2563EB]/5 border-[#2563EB]/20" : "bg-[#F8FAFC] border-border/60"}`}>
                <p>{r.message}</p>
                <p className="text-xs text-muted-foreground mt-1.5">— {r.by} · {fmtDate(r.at)}</p>
              </div>
            ))}
          </div>
          {isAdmin && (
            <Select value={replyStatus} onValueChange={setReplyStatus}>
              <SelectTrigger data-testid="reply-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["open", "in_progress", "resolved", "closed"].map((s) => (
                  <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply…" data-testid="reply-message" />
          <Button onClick={sendReply} disabled={!reply.trim()} className="w-full rounded-full bg-[#0B1B3D]" data-testid="reply-submit-btn">
            <MessageSquare className="w-4 h-4 mr-1.5" /> Send Reply
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
